import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';
import {
  ActionButton,
  IconButton,
  Segments,
  TaskChoice,
  TaskField,
} from '../../components/tasks/TaskControls';
import CategoryScope from '../../components/tasks/CategoryScope';
import NotificationGroups from '../../components/tasks/NotificationGroups';
import { getCategories } from '../../services/inventoryService';
import {
  Dashboard,
  dashboard,
  newRequestId,
  operation,
  taskAction,
} from '../../services/operationsService';
import {
  disablePush,
  enablePush,
  PushProgress,
} from '../../services/pushService';
import { Category } from '../../types';
import { OperationalTask } from '../../types/operations';
import {
  notificationStoreId,
  notificationTaskId,
  taskFormErrors,
} from '../../utils/taskPresentation';

const empty: Dashboard = {
  tasks: [],
  batches: [],
  stores: [],
  positions: [],
  profiles: [],
  templates: [],
  notifications: [],
  movements: [],
  alertDays: [90, 30, 7],
};
const closed = (task: OperationalTask) =>
  ['completed', 'cancelled'].includes(task.status);

export default function TasksScreen() {
  const { appUser } = useAuth();
  const { t } = useLanguage();
  const router = useRouter();
  const tr = (key: string) => t('operations.' + key);
  const manager = appUser?.role === 'admin' || appUser?.role === 'manager';
  const [tab, setTab] = useState('tasks');
  const [storeFilter, setStoreFilter] = useState('');
  const [taskFilter, setTaskFilter] = useState('open');
  const [onlyMine, setOnlyMine] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(true);
  const [search, setSearch] = useState('');
  const [taskLimit, setTaskLimit] = useState(20);
  const [showTemplates, setShowTemplates] = useState(false);
  const [data, setData] = useState<Dashboard>(empty);
  const [categories, setCategories] = useState<Category[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [modal, setModal] = useState<'create' | 'detail' | null>(null);
  const [selected, setSelected] = useState<OperationalTask | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [scope, setScope] = useState('all');
  const [requestId, setRequestId] = useState(newRequestId);
  const [thresholds, setThresholds] = useState('90,30,7');
  const [pushProgress, setPushProgress] = useState<PushProgress | null>(null);
  const [pushEnabled, setPushEnabled] = useState<boolean | null>(null);
  const sequence = useRef(0);
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const stores = [...data.stores].sort((a, b) => a.name.localeCompare(b.name));
  const storeName = (id: string) =>
    stores.find((store) => store.id === id)?.name ?? tr('store');
  const assigneeName = (id: string | null) =>
    data.profiles.find((person) => person.id === id)?.name ??
    (id === appUser?.uid
      ? tr('taskYou')
      : id
        ? tr('taskAssignedUser')
        : tr('unassigned'));
  const unread = data.notifications.filter(
    (item) => item.active && !item.read,
  ).length;
  const waiting = data.tasks.filter((task) => !closed(task));
  const overdue = waiting.filter((task) => task.dueOn < today);
  const filteredTasks = data.tasks
    .filter(
      (task) =>
        (!storeFilter || task.storeId === storeFilter) &&
        (!onlyMine || task.assigneeId === appUser?.uid) &&
        (taskFilter === 'all' ||
          (taskFilter === 'closed'
            ? closed(task)
            : taskFilter === 'overdue'
              ? !closed(task) && task.dueOn < today
              : !closed(task))) &&
        (task.title + ' ' + storeName(task.storeId))
          .toLocaleLowerCase()
          .includes(search.trim().toLocaleLowerCase()),
    )
    .sort(
      (a, b) =>
        Number(closed(a)) - Number(closed(b)) ||
        a.dueOn.localeCompare(b.dueOn) ||
        a.title.localeCompare(b.title),
    );
  const notifications = data.notifications.filter(
    (item) =>
      item.active &&
      (!onlyUnread || !item.read) &&
      (!storeFilter ||
        notificationStoreId(item.href) === storeFilter ||
        data.tasks.some(
          (task) =>
            task.id === notificationTaskId(item.eventId) &&
            task.storeId === storeFilter,
        )),
  );
  const templates = data.templates.filter(
    (template) => !storeFilter || template.storeId === storeFilter,
  );

  const reload = useCallback(async () => {
    const current = ++sequence.current;
    const [next, cats] = await Promise.all([dashboard(), getCategories()]);
    if (current !== sequence.current) return;
    setData(next);
    setCategories(cats);
    setThresholds(next.alertDays.join(','));
    setLoaded(true);
  }, []);
  useEffect(() => {
    if (!appUser || appUser.role === 'guest') return;
    const requestState = sequence;
    let active = true;
    setModal(null);
    setSelected(null);
    setStoreFilter('');
    setLoading(true);
    setLoaded(false);
    setData(empty);
    setError('');
    reload()
      .catch(() => {
        if (active) setError(tr('error'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      requestState.current++;
    };
  }, [appUser?.uid, appUser?.role, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  const message = (error: unknown) => {
    const text = error instanceof Error ? error.message : String(error);
    const codes: Record<string, string> = {
      PUSH_UNAVAILABLE: 'pushDenied',
      PUSH_CONFIG_MISSING: 'pushConfigMissing',
      PUSH_PERMISSION_TIMEOUT: 'pushPermissionTimeout',
      PUSH_PERMISSION_DENIED: 'pushDenied',
      PUSH_WORKER_TIMEOUT: 'pushWorkerTimeout',
      PUSH_WORKER_FAILED: 'pushWorkerTimeout',
      PUSH_TOKEN_TIMEOUT: 'pushTokenTimeout',
      PUSH_SAVE_TIMEOUT: 'pushSaveTimeout',
      INITIALIZE_STORE: 'initializeFirst',
      SPLIT_STOCKTAKING: 'split',
      STOCKTAKING_CONFLICT: 'conflict',
      RESULT_REQUIRED: 'reasonRequired',
      INVALID_ASSIGNEE: 'taskAssigneeRequired',
    };
    const match = Object.entries(codes).find(([code]) => text.includes(code));
    return tr(match?.[1] ?? 'error');
  };
  const run = async (
    fn: () => Promise<unknown>,
    close = false,
    success = '',
  ) => {
    if (busy || loading) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      if (close) setModal(null);
      if (success) setNotice(success);
      await reload();
    } catch (error) {
      const detail = message(error);
      setError(detail);
      CrossPlatformAlert.alert(tr('error'), detail);
    } finally {
      setBusy(false);
    }
  };
  const change = (key: string) => (value: string) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    setFormErrors((previous) => ({ ...previous, [key]: '' }));
  };
  const openCreate = () => {
    setForm({
      type: 'stocktaking',
      storeId: storeFilter,
      titleLabel: '',
      description: '',
      dueOn: today,
      recurring: 'no',
      interval: '30',
      assignee: '',
    });
    setCategoryIds([]);
    setScope('all');
    setFormErrors({});
    setRequestId(newRequestId());
    setModal('create');
    setError('');
  };
  const openTask = (task: OperationalTask) => {
    if (busy) return;
    setSelected(task);
    setManageOpen(false);
    setError('');
    setForm({
      result: task.result ?? '',
      assignee: task.assigneeId ?? '',
      dueOn: task.dueOn,
    });
    setFormErrors({});
    setModal('detail');
  };
  const saveTask = () => {
    const errors = taskFormErrors(form, scope, categoryIds);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    void run(
      () =>
        operation('taskOperation', {
          action: 'create',
          requestId,
          storeId: form.storeId,
          title: form.titleLabel.trim(),
          description: form.description.trim(),
          type: form.type,
          dueOn: form.dueOn,
          assigneeId: form.recurring === 'yes' ? null : form.assignee || null,
          intervalDays: form.recurring === 'yes' ? Number(form.interval) : 0,
          categoryIds:
            form.type === 'stocktaking' && scope === 'selected'
              ? categoryIds
              : [],
        }),
      true,
      tr('taskCreated'),
    );
  };
  const readNotifications = (ids: string[]) =>
    void run(async () => {
      // Apply successful reads even if another item fails, without adding a new API.
      let failure: unknown;
      for (let index = 0; index < ids.length; index += 5) {
        const part = ids.slice(index, index + 5);
        const results = await Promise.allSettled(
          part.map((notificationId) =>
            operation('notificationOperation', {
              action: 'read',
              notificationId,
            }),
          ),
        );
        const read = new Set(
          part.filter((_, offset) => results[offset].status === 'fulfilled'),
        );
        setData((previous) => ({
          ...previous,
          notifications: previous.notifications.map((item) =>
            read.has(item.id) ? { ...item, read: true } : item,
          ),
        }));
        const failed = results.find((result) => result.status === 'rejected');
        if (failed?.status === 'rejected') failure = failed.reason;
      }
      if (failure) throw failure;
    });
  const enableNotifications = async () => {
    if (busy || loading) return;
    setBusy(true);
    setError('');
    try {
      await enablePush(setPushProgress);
      setPushEnabled(true);
      setNotice(tr('pushEnabled'));
    } catch (error) {
      setError(message(error));
    } finally {
      setBusy(false);
      setPushProgress(null);
    }
  };
  const cancelTask = () => {
    if (!selected) return;
    CrossPlatformAlert.alert(tr('cancelTask'), tr('taskCancelHint'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: tr('cancelTask'),
        style: 'destructive',
        onPress: () => void run(() => taskAction(selected.id, 'cancel'), true),
      },
    ]);
  };
  const dateField = (key: string) => (
    <TaskField
      label={tr(key)}
      value={form[key] ?? ''}
      onChange={change(key)}
      date
      error={formErrors[key] ? tr(formErrors[key]) : undefined}
    />
  );
  const badge = (task: OperationalTask) => (
    <View style={s.rowHeading}>
      <Text
        style={[
          s.badge,
          task.status === 'completed'
            ? s.badgeDone
            : task.status === 'cancelled'
              ? s.badgeClosed
              : s.badgeOpen,
        ]}
      >
        {tr(task.status)}
      </Text>
      {task.dueOn < today && !closed(task) && (
        <Text style={[s.badge, s.badgeOverdue]}>{tr('overdue')}</Text>
      )}
    </View>
  );

  if (!appUser || appUser.role === 'guest')
    return <Text style={s.empty}>{tr('noAccess')}</Text>;
  return (
    <View style={s.page}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={s.content}
      >
        <View style={s.headerBlock}>
          <View style={s.headingRow}>
            <View style={s.flex}>
              <Text style={s.title}>{tr('title')}</Text>
            </View>
            <IconButton
              label={tr('refresh')}
              icon="refresh-outline"
              disabled={busy || loading}
              onPress={() => void run(async () => {})}
            />
            {manager && tab === 'tasks' && (
              <ActionButton
                title={tr('newTask')}
                icon="add"
                tone="primary"
                disabled={busy || loading || !stores.length}
                onPress={openCreate}
              />
            )}
          </View>
          <Text style={s.muted}>
            {tr(
              tab === 'notifications'
                ? 'taskNotificationsHint'
                : 'taskPageHint',
            )}
          </Text>
        </View>
        <View style={s.tabs}>
          {['tasks', 'notifications', 'settings'].map((value) => (
            <Pressable
              key={value}
              accessibilityRole="tab"
              accessibilityState={{ selected: tab === value }}
              aria-selected={tab === value}
              onPress={() => {
                setTab(value);
                if (loaded) setError('');
              }}
              style={[s.tab, tab === value && s.tabActive]}
            >
              <Text style={[s.tabText, tab === value && s.tabTextActive]}>
                {tr(value)}
              </Text>
              {value === 'notifications' && unread > 0 && (
                <Text style={s.tabCount}>{unread}</Text>
              )}
            </Pressable>
          ))}
        </View>
        {!!error && (
          <View accessibilityRole="alert" style={s.errorBox}>
            <Text style={s.error}>{error}</Text>
          </View>
        )}
        {!!notice && (
          <View accessibilityLiveRegion="polite" style={s.notice}>
            <Text style={s.flex}>{notice}</Text>
            <IconButton
              label={tr('close')}
              icon="close-outline"
              onPress={() => setNotice('')}
            />
          </View>
        )}
        {loading ? (
          <View style={s.emptyState}>
            <ActivityIndicator
              accessibilityLabel={tr('loading')}
              color="#2563EB"
            />
          </View>
        ) : (
          <>
            {tab !== 'settings' && (
              <View style={s.filterPanel}>
                <View style={s.filterColumn}>
                  <TaskChoice
                    label={tr('taskFilterStore')}
                    value={storeFilter}
                    options={[{ id: '', name: tr('taskAllStores') }, ...stores]}
                    onChange={(value) => {
                      setStoreFilter(value);
                      setTaskLimit(20);
                    }}
                  />
                </View>
                <View style={s.filterColumn}>
                  <Segments
                    label={tr(
                      tab === 'tasks' ? 'taskListFilter' : 'notifications',
                    )}
                    value={
                      tab === 'tasks'
                        ? taskFilter
                        : onlyUnread
                          ? 'unread'
                          : 'all'
                    }
                    onChange={(value) => {
                      if (tab === 'tasks') {
                        setTaskFilter(value);
                        setTaskLimit(20);
                      } else setOnlyUnread(value === 'unread');
                    }}
                    options={
                      tab === 'tasks'
                        ? [
                            { id: 'open', name: tr('taskOpen') },
                            { id: 'overdue', name: tr('overdue') },
                            { id: 'closed', name: tr('taskClosed') },
                            { id: 'all', name: tr('taskAll') },
                          ]
                        : [
                            { id: 'unread', name: tr('taskUnread') },
                            { id: 'all', name: tr('taskAll') },
                          ]
                    }
                  />
                </View>
              </View>
            )}
            {tab === 'tasks' && (
              <>
                <View style={s.listToolbar}>
                  <View style={s.searchBox}>
                    <Ionicons name="search" size={18} color="#64748B" />
                    <TextInput
                      accessibilityLabel={tr('taskSearchTasks')}
                      placeholder={tr('taskSearchTasks')}
                      value={search}
                      onChangeText={(value) => {
                        setSearch(value);
                        setTaskLimit(20);
                      }}
                      style={s.searchInput}
                    />
                  </View>
                  <Pressable
                    accessibilityRole="checkbox"
                    accessibilityLabel={tr('onlyMine')}
                    accessibilityState={{ checked: onlyMine }}
                    aria-checked={onlyMine}
                    onPress={() => {
                      setOnlyMine(!onlyMine);
                      setTaskLimit(20);
                    }}
                    style={s.mine}
                  >
                    <Ionicons
                      name={onlyMine ? 'checkbox' : 'square-outline'}
                      size={21}
                      color={onlyMine ? '#2563EB' : '#64748B'}
                    />
                    <Text style={s.meta}>{tr('taskMineShort')}</Text>
                  </Pressable>
                </View>
                <Text style={s.muted}>
                  {tr('taskOpen')}: {waiting.length} · {tr('overdue')}:{' '}
                  {overdue.length} · {tr('taskResults')}: {filteredTasks.length}
                </Text>
                {filteredTasks.length ? (
                  <View style={s.list}>
                    {filteredTasks.slice(0, taskLimit).map((task) => (
                      <Pressable
                        key={task.id}
                        accessibilityRole="button"
                        accessibilityLabel={task.title}
                        accessibilityState={{ disabled: busy }}
                        aria-disabled={busy}
                        disabled={busy}
                        onPress={() => openTask(task)}
                        style={s.taskRow}
                      >
                        <View style={s.taskIcon}>
                          <Ionicons
                            name={
                              task.type === 'stocktaking'
                                ? 'clipboard-outline'
                                : 'checkmark-circle-outline'
                            }
                            size={21}
                            color="#475569"
                          />
                        </View>
                        <View style={s.flex}>
                          <View style={s.rowHeading}>
                            <Text style={s.taskTitle}>{task.title}</Text>
                            {badge(task)}
                          </View>
                          <Text style={s.meta}>
                            {storeName(task.storeId)} · {task.dueOn}
                          </Text>
                          <Text style={s.small}>
                            {assigneeName(task.assigneeId)}
                            {task.templateId ? ' · ' + tr('taskRecurring') : ''}
                          </Text>
                        </View>
                        <Ionicons
                          name="chevron-forward"
                          size={18}
                          color="#94A3B8"
                        />
                      </Pressable>
                    ))}
                  </View>
                ) : (
                  loaded && (
                    <View style={s.emptyState}>
                      <Ionicons
                        name="checkmark-circle-outline"
                        size={30}
                        color="#94A3B8"
                      />
                      <Text style={s.sectionTitle}>{tr('taskNoTasks')}</Text>
                      <Text style={s.muted}>
                        {tr(
                          manager ? 'taskNoTasksHint' : 'taskNoTasksUserHint',
                        )}
                      </Text>
                    </View>
                  )
                )}
                {filteredTasks.length > taskLimit && (
                  <ActionButton
                    title={tr('taskShowMore')}
                    onPress={() => setTaskLimit(taskLimit + 20)}
                  />
                )}
                {!!templates.length && (
                  <View style={s.section}>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityState={{ expanded: showTemplates }}
                      aria-expanded={showTemplates}
                      onPress={() => setShowTemplates(!showTemplates)}
                      style={s.disclosure}
                    >
                      <Ionicons
                        name="repeat-outline"
                        size={19}
                        color="#475569"
                      />
                      <Text style={s.sectionTitle}>
                        {tr('taskRecurring')} ({templates.length})
                      </Text>
                      <Ionicons
                        name={showTemplates ? 'chevron-up' : 'chevron-down'}
                        size={18}
                        color="#64748B"
                      />
                    </Pressable>
                    {showTemplates &&
                      templates.map((template) => (
                        <View style={s.templateRow} key={template.id}>
                          <Text style={s.taskTitle}>{template.title}</Text>
                          <Text style={s.meta}>
                            {storeName(template.storeId)} ·{' '}
                            {tr('taskEveryDays')}: {template.intervalDays}
                          </Text>
                          <Text style={s.small}>
                            {tr('nextDue')}: {template.nextDueOn} ·{' '}
                            {tr('lastCompleted')}:{' '}
                            {template.lastCompletedOn ?? tr('never')} ·{' '}
                            {tr(template.enabled ? 'active' : 'cancelled')}
                          </Text>
                        </View>
                      ))}
                  </View>
                )}
              </>
            )}
            {tab === 'notifications' && (
              <>
                {notifications.length ? (
                  <NotificationGroups
                    notifications={notifications}
                    stores={stores}
                    busy={busy}
                    taskTitles={
                      new Map(data.tasks.map((task) => [task.id, task.title]))
                    }
                    onRead={readNotifications}
                    onStore={(storeId) =>
                      router.push({
                        pathname: '/(app)/stores',
                        params: { storeId },
                      })
                    }
                    onTask={(id) => {
                      const task = data.tasks.find((task) => task.id === id);
                      if (task) openTask(task);
                      else {
                        setTab('tasks');
                        setTaskFilter('all');
                        setOnlyMine(false);
                        setStoreFilter('');
                      }
                    }}
                  />
                ) : loaded ? (
                  <View style={s.emptyState}>
                    <Ionicons
                      name="notifications-outline"
                      size={30}
                      color="#94A3B8"
                    />
                    <Text style={s.sectionTitle}>
                      {tr('taskNoNotifications')}
                    </Text>
                    <Text style={s.muted}>{tr('taskNoNotificationsHint')}</Text>
                  </View>
                ) : null}
              </>
            )}
            {tab === 'settings' && (
              <>
                <View style={s.section}>
                  <Text style={s.sectionTitle}>{tr('taskPushSettings')}</Text>
                  <Text style={s.muted}>{tr('taskPushHint')}</Text>
                  {pushEnabled !== null && (
                    <Text style={s.small}>
                      {tr(pushEnabled ? 'pushEnabled' : 'taskPushDisabled')}
                    </Text>
                  )}
                  <View style={s.inlineActions}>
                    <ActionButton
                      title={tr('enablePush')}
                      icon="notifications-outline"
                      disabled={busy}
                      onPress={() => void enableNotifications()}
                    />
                    <ActionButton
                      title={tr('disablePush')}
                      tone="quiet"
                      disabled={busy}
                      onPress={() =>
                        void run(
                          async () => {
                            await disablePush();
                            setPushEnabled(false);
                          },
                          false,
                          tr('taskPushDisabled'),
                        )
                      }
                    />
                  </View>
                  {pushProgress && (
                    <Text accessibilityLiveRegion="polite" style={s.muted}>
                      {tr('pushProgress_' + pushProgress)}
                    </Text>
                  )}
                </View>
                {appUser.role === 'admin' && (
                  <View style={s.section}>
                    <Text style={s.sectionTitle}>
                      {tr('taskAlertSettings')}
                    </Text>
                    <TaskField
                      label={tr('thresholds')}
                      hint={tr('taskThresholdHint')}
                      value={thresholds}
                      onChange={setThresholds}
                    />
                    <ActionButton
                      title={tr('save')}
                      icon="checkmark-outline"
                      disabled={busy}
                      onPress={() =>
                        void run(
                          () =>
                            operation('notificationOperation', {
                              action: 'thresholds',
                              days: thresholds.split(',').map(Number),
                            }),
                          false,
                          tr('saved'),
                        )
                      }
                    />
                  </View>
                )}
              </>
            )}
          </>
        )}
      </ScrollView>
      <Modal
        accessibilityLabel={tr(modal === 'create' ? 'newTask' : 'taskDetails')}
        visible={!!modal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (!busy) setModal(null);
        }}
      >
        <KeyboardAvoidingView
          style={s.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <View style={s.modal}>
            <View style={s.modalHeader}>
              <Text style={s.sectionTitle}>
                {tr(modal === 'create' ? 'newTask' : 'taskDetails')}
              </Text>
              <IconButton
                label={tr('close')}
                icon="close-outline"
                disabled={busy}
                onPress={() => setModal(null)}
              />
            </View>
            <ScrollView
              keyboardShouldPersistTaps="handled"
              style={s.modalScroll}
              contentContainerStyle={s.modalContent}
            >
              {!!error && (
                <Text accessibilityRole="alert" style={s.error}>
                  {error}
                </Text>
              )}
              {modal === 'create' && (
                <>
                  <Text style={s.muted}>{tr('taskFormHint')}</Text>
                  <Segments
                    label={tr('type')}
                    value={form.type}
                    onChange={change('type')}
                    options={['stocktaking', 'general'].map((id) => ({
                      id,
                      name: tr(id),
                    }))}
                  />
                  <TaskChoice
                    label={tr('store')}
                    placeholder={tr('taskChooseStore')}
                    value={form.storeId ?? ''}
                    options={stores}
                    onChange={change('storeId')}
                    error={
                      formErrors.storeId ? tr(formErrors.storeId) : undefined
                    }
                  />
                  <TaskField
                    label={tr('titleLabel')}
                    value={form.titleLabel ?? ''}
                    onChange={change('titleLabel')}
                    error={
                      formErrors.titleLabel
                        ? tr(formErrors.titleLabel)
                        : undefined
                    }
                  />
                  <TaskField
                    label={tr('taskDescriptionOptional')}
                    value={form.description ?? ''}
                    onChange={change('description')}
                    multiline
                  />
                  <View style={s.formSection}>
                    <Text style={s.sectionTitle}>{tr('taskSchedule')}</Text>
                    {dateField('dueOn')}
                    <View style={s.switchRow}>
                      <View style={s.flex}>
                        <Text style={s.label}>{tr('taskRepeat')}</Text>
                        <Text style={s.small}>{tr('taskRepeatHint')}</Text>
                      </View>
                      <Switch
                        accessibilityLabel={tr('taskRepeat')}
                        value={form.recurring === 'yes'}
                        onValueChange={(value) =>
                          change('recurring')(value ? 'yes' : 'no')
                        }
                      />
                    </View>
                    {form.recurring === 'yes' ? (
                      <TaskField
                        label={tr('taskEveryDays')}
                        value={form.interval ?? ''}
                        onChange={change('interval')}
                        numeric
                        hint={tr('taskRecurringHint')}
                        error={
                          formErrors.interval
                            ? tr(formErrors.interval)
                            : undefined
                        }
                      />
                    ) : (
                      <TaskChoice
                        label={tr('assignee')}
                        value={form.assignee ?? ''}
                        options={[
                          { id: '', name: tr('unassigned') },
                          ...data.profiles,
                        ]}
                        onChange={change('assignee')}
                      />
                    )}
                  </View>
                  {form.type === 'stocktaking' && (
                    <View style={s.formSection}>
                      <CategoryScope
                        categories={categories}
                        mode={scope}
                        onMode={(value) => {
                          setScope(value);
                          setFormErrors((previous) => ({
                            ...previous,
                            scope: '',
                          }));
                        }}
                        value={categoryIds}
                        onChange={(value) => {
                          setCategoryIds(value);
                          setFormErrors((previous) => ({
                            ...previous,
                            scope: '',
                          }));
                        }}
                        error={
                          formErrors.scope ? tr(formErrors.scope) : undefined
                        }
                      />
                    </View>
                  )}
                </>
              )}
              {modal === 'detail' && selected && (
                <>
                  <View style={s.rowHeading}>
                    <Text style={s.title}>{selected.title}</Text>
                    {badge(selected)}
                  </View>
                  <View style={s.metadata}>
                    <Text style={s.meta}>
                      {tr('store')}: {storeName(selected.storeId)}
                    </Text>
                    <Text style={s.meta}>
                      {tr('assignee')}: {assigneeName(selected.assigneeId)}
                    </Text>
                    <Text style={s.meta}>
                      {tr('dueOn')}: {selected.dueOn}
                    </Text>
                    <Text style={s.meta}>
                      {tr('type')}: {tr(selected.type)}
                    </Text>
                    {selected.type === 'stocktaking' && (
                      <Text style={s.meta}>
                        {tr('scope')}:{' '}
                        {selected.categoryIds.length
                          ? selected.categoryIds
                              .map(
                                (id) =>
                                  categories.find(
                                    (category) => category.id === id,
                                  )?.name ?? id,
                              )
                              .join(', ')
                          : tr('taskWholeStore')}
                      </Text>
                    )}
                  </View>
                  {!!selected.description && (
                    <Text style={s.description}>{selected.description}</Text>
                  )}
                  {!!selected.result && (
                    <View style={s.formSection}>
                      <Text style={s.label}>{tr('result')}</Text>
                      <Text style={s.description}>{selected.result}</Text>
                    </View>
                  )}
                  {selected.assigneeId === appUser.uid && !closed(selected) && (
                    <View style={s.formSection}>
                      {selected.type === 'stocktaking' ? (
                        <>
                          <Text style={s.muted}>
                            {tr('taskWizardHintShort')}
                          </Text>
                          <ActionButton
                            title={tr('taskPerformCount')}
                            icon="clipboard-outline"
                            tone="primary"
                            disabled={busy}
                            onPress={() => {
                              setModal(null);
                              router.push({
                                pathname: '/(app)/usage',
                                params: {
                                  taskId: selected.id,
                                  storeId: selected.storeId,
                                  type: 'stocktaking',
                                  create: '1',
                                },
                              });
                            }}
                          />
                        </>
                      ) : selected.status === 'assigned' ? (
                        <ActionButton
                          title={tr('start')}
                          icon="play-outline"
                          tone="primary"
                          disabled={busy}
                          onPress={() =>
                            void run(async () => {
                              await taskAction(selected.id, 'start');
                              setSelected({
                                ...selected,
                                status: 'in_progress',
                              });
                            })
                          }
                        />
                      ) : (
                        <>
                          <TaskField
                            label={tr('result')}
                            value={form.result ?? ''}
                            onChange={change('result')}
                            multiline
                          />
                          <ActionButton
                            title={tr('complete')}
                            icon="checkmark-done-outline"
                            tone="primary"
                            disabled={busy || !form.result?.trim()}
                            onPress={() =>
                              void run(
                                () =>
                                  taskAction(selected.id, 'complete', {
                                    result: form.result.trim(),
                                  }),
                                true,
                                tr('saved'),
                              )
                            }
                          />
                        </>
                      )}
                    </View>
                  )}
                  {manager && (
                    <View style={s.formSection}>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityState={{ expanded: manageOpen }}
                        aria-expanded={manageOpen}
                        onPress={() => setManageOpen(!manageOpen)}
                        style={s.disclosure}
                      >
                        <Text style={s.sectionTitle}>
                          {tr('taskManagement')}
                        </Text>
                        <Ionicons
                          name={manageOpen ? 'chevron-up' : 'chevron-down'}
                          size={18}
                          color="#64748B"
                        />
                      </Pressable>
                      {manageOpen && (
                        <>
                          {!closed(selected) && (
                            <>
                              <TaskChoice
                                label={tr('assignee')}
                                value={form.assignee ?? ''}
                                options={data.profiles}
                                onChange={change('assignee')}
                                placeholder={tr('taskChooseAssignee')}
                              />
                              <ActionButton
                                title={tr('assign')}
                                icon="person-add-outline"
                                disabled={
                                  busy ||
                                  !form.assignee ||
                                  form.assignee === selected.assigneeId
                                }
                                onPress={() =>
                                  void run(
                                    () =>
                                      taskAction(selected.id, 'assign', {
                                        assigneeId: form.assignee,
                                      }),
                                    true,
                                    tr('saved'),
                                  )
                                }
                              />
                              {dateField('dueOn')}
                              <ActionButton
                                title={tr('taskSaveDate')}
                                icon="calendar-outline"
                                disabled={
                                  busy ||
                                  !form.dueOn ||
                                  form.dueOn === selected.dueOn
                                }
                                onPress={() =>
                                  void run(
                                    () =>
                                      taskAction(selected.id, 'due', {
                                        dueOn: form.dueOn,
                                      }),
                                    true,
                                    tr('saved'),
                                  )
                                }
                              />
                              <ActionButton
                                title={tr('cancelTask')}
                                icon="close-circle-outline"
                                tone="danger"
                                disabled={busy}
                                onPress={cancelTask}
                              />
                            </>
                          )}
                          {selected.templateId &&
                            selected.status === 'cancelled' && (
                              <ActionButton
                                title={tr('reissue')}
                                icon="reload-outline"
                                disabled={busy}
                                onPress={() =>
                                  void run(
                                    () => taskAction(selected.id, 'reissue'),
                                    true,
                                    tr('saved'),
                                  )
                                }
                              />
                            )}
                          {selected.templateId && (
                            <ActionButton
                              title={tr('disableTemplate')}
                              icon="pause-outline"
                              disabled={busy}
                              onPress={() =>
                                void run(
                                  () =>
                                    taskAction(selected.id, 'disableTemplate'),
                                  true,
                                  tr('saved'),
                                )
                              }
                            />
                          )}
                          {closed(selected) && !selected.templateId && (
                            <Text style={s.muted}>{tr('taskClosedHint')}</Text>
                          )}
                        </>
                      )}
                    </View>
                  )}
                </>
              )}
            </ScrollView>
            {modal === 'create' && (
              <View style={s.modalFooter}>
                <ActionButton
                  title={t('common.cancel')}
                  disabled={busy}
                  onPress={() => setModal(null)}
                />
                <ActionButton
                  title={tr('taskCreate')}
                  icon="checkmark-outline"
                  tone="primary"
                  disabled={busy}
                  loading={busy}
                  onPress={saveTask}
                />
              </View>
            )}
            {modal === 'detail' && busy && (
              <ActivityIndicator
                accessibilityLabel={tr('loading')}
                color="#2563EB"
                style={s.progress}
              />
            )}
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
}

const s = StyleSheet.create({
  page: { flex: 1, backgroundColor: '#F8FAFC' },
  content: {
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
    padding: 16,
    paddingBottom: 40,
    gap: 14,
  },
  headingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  headerBlock: { gap: 4 },
  flex: { flex: 1, minWidth: 0 },
  title: { fontSize: 22, fontWeight: '700', color: '#0F172A', flexShrink: 1 },
  muted: { color: '#64748B', fontSize: 13, lineHeight: 20 },
  sectionTitle: { color: '#0F172A', fontSize: 16, fontWeight: '600' },
  label: { color: '#334155', fontSize: 13, fontWeight: '600' },
  meta: { color: '#475569', fontSize: 13, lineHeight: 19 },
  small: { color: '#64748B', fontSize: 12, lineHeight: 18 },
  description: { color: '#334155', fontSize: 14, lineHeight: 21 },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#E2E8F0',
    gap: 2,
  },
  tab: {
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 10,
    minHeight: 44,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: { borderBottomColor: '#2563EB' },
  tabText: { color: '#64748B', fontSize: 13 },
  tabTextActive: { color: '#1D4ED8', fontWeight: '600' },
  tabCount: {
    color: '#1D4ED8',
    backgroundColor: '#DBEAFE',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    fontSize: 11,
  },
  filterPanel: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  filterColumn: { flex: 1, minWidth: 220 },
  listToolbar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
  },
  searchBox: {
    flex: 1,
    minWidth: 200,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  searchInput: { flex: 1, minHeight: 44, fontSize: 14, color: '#0F172A' },
  mine: { flexDirection: 'row', alignItems: 'center', gap: 7, minHeight: 44 },
  list: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  taskRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    padding: 14,
    borderBottomWidth: 1,
    borderColor: '#E2E8F0',
  },
  taskIcon: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
  },
  rowHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  taskTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    flexShrink: 1,
  },
  badge: {
    alignSelf: 'flex-start',
    borderRadius: 5,
    paddingHorizontal: 7,
    paddingVertical: 3,
    fontSize: 11,
    fontWeight: '600',
    overflow: 'hidden',
  },
  badgeOpen: { color: '#1D4ED8', backgroundColor: '#EFF6FF' },
  badgeDone: { color: '#166534', backgroundColor: '#F0FDF4' },
  badgeClosed: { color: '#475569', backgroundColor: '#F1F5F9' },
  badgeOverdue: { color: '#9A3412', backgroundColor: '#FFF7ED' },
  section: {
    padding: 16,
    gap: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
  },
  disclosure: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    minHeight: 44,
  },
  templateRow: {
    gap: 4,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderColor: '#E2E8F0',
  },
  emptyState: {
    alignItems: 'center',
    padding: 28,
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
  },
  empty: { padding: 24, textAlign: 'center', color: '#64748B' },
  inlineActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  errorBox: { padding: 12, borderRadius: 8, backgroundColor: '#FEF2F2' },
  error: { color: '#B91C1C', fontSize: 13 },
  notice: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 12,
    backgroundColor: '#F0FDF4',
    borderRadius: 8,
  },
  overlay: {
    flex: 1,
    backgroundColor: '#0F172A66',
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    width: '100%',
    maxWidth: 680,
    maxHeight: '94%',
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    overflow: 'hidden',
    flexShrink: 1,
  },
  modalHeader: {
    paddingLeft: 20,
    paddingRight: 8,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderColor: '#E2E8F0',
  },
  modalScroll: { flexGrow: 0 },
  modalContent: { padding: 20, gap: 18 },
  modalFooter: {
    padding: 14,
    borderTopWidth: 1,
    borderColor: '#E2E8F0',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  formSection: {
    borderTopWidth: 1,
    borderColor: '#E2E8F0',
    paddingTop: 16,
    gap: 14,
  },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  metadata: {
    gap: 5,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#F8FAFC',
  },
  progress: { padding: 12 },
});
